import { ChangeDetectorRef, Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '../../../auth/auth.service';
import { NotifyService } from '../../../common/notify/notify.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, NgModel, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ParticipantService } from '../../../services/participant.service';
import { Recipient } from '../../../interfaces/recipient';
import { Approval, ApprovalColumn, ApprovalColumnLabels, ApprovalStatus } from '../../../interfaces/approval';
import { MatButtonModule } from '@angular/material/button';
import { NgxEditorModule, Toolbar, Editor, toHTML } from 'ngx-editor';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-request-priority',
  imports: [MatDialogModule, MatFormFieldModule, CommonModule, ReactiveFormsModule, FormsModule, MatOptionModule, MatSelectModule, MatInputModule, MatButtonModule, MatCheckboxModule, NgxEditorModule, MatButtonToggleModule, MatIconModule, MatTooltipModule],
  providers: [NotifyService],
  templateUrl: './request-priority.component.html',
  styleUrl: './request-priority.component.scss'
})
export class RequestPriorityComponent implements OnInit, OnDestroy {
  constructor(
    private readonly authService: AuthService,
    private readonly participantService: ParticipantService,
    private readonly ref: MatDialogRef<RequestPriorityComponent>,
    private readonly notify: NotifyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) readonly recipient: Recipient
  ) {}

  privileges = Object.values(ApprovalColumn); // Get enum keys
  approvalColumnLabels = ApprovalColumnLabels; // Label mapping
  selectedPrivilege: ApprovalColumn | null = null;
  approval: Approval | null = null;

  value?: string;
  approvalReason?: string;
  showValueInput = false;
  showTextInput = false;
  showCheckboxInput = false;

  // New fields for grouped priority request
  requestForm!: FormGroup;
  requestAcceptableHla = false;
  requestAcceptableBloodTypes = false;

  // Request type toggle
  requestType: 'priority' | 'extraWaitTime' = 'priority';
  ApprovalColumn = ApprovalColumn; // Expose enum to template

  // Track if there's a pending extra waittime request
  hasPendingExtraWaitTimeRequest = false;
  // Track if there's a pending priority request
  hasPendingPriorityRequest = false;

  // Rich text editor
  editor!: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    ['link'],
    ['undo', 'redo']
  ];

  ngOnInit(): void {
    this.editor = new Editor();
    this.requestForm = this.fb.group({
      requestComment: [null, [Validators.required, this.notEmptyEditorValidator]],
      requestedAcceptableHla: [''],
      requestedAcceptableBloodTypes: [[]]
    });

    // Check for pending requests
    this.checkPendingRequests();
  }

  ngOnDestroy(): void {
    if (this.editor) {
      this.editor.destroy();
    }
  }

  /**
   * Check if there are any pending requests for this recipient
   */
  private checkPendingRequests(): void {
    this.participantService.getAllApprovals().subscribe({
      next: (approvals: Approval[]) => {
        // Check for pending extra waittime request
        this.hasPendingExtraWaitTimeRequest = approvals.some(
          (approval) =>
            approval.recipient?.id === this.recipient.id &&
            approval.approvalColumn === ApprovalColumn.EXTRAWAITTIME &&
            approval.status === ApprovalStatus.PENDING
        );

        // Check for pending priority request
        this.hasPendingPriorityRequest = approvals.some(
          (approval) =>
            approval.recipient?.id === this.recipient.id &&
            approval.approvalColumn === ApprovalColumn.PRIORITY &&
            approval.status === ApprovalStatus.PENDING
        );
      },
      error: (error) => {
        console.error('Error fetching approvals:', error);
        this.hasPendingExtraWaitTimeRequest = false;
        this.hasPendingPriorityRequest = false;
      }
    });
  }

  /**
   * Custom validator to check if ngx-editor content is not empty
   */
  private notEmptyEditorValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return { required: true };
    }

    const html = toHTML(control.value);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const stripped = (doc.body.textContent ?? '').trim();

    if (stripped.length === 0) {
      return { required: true };
    }

    return null;
  }

  async apply() {
    // PM waitlist patients cannot have priority or extra wait time
    if (this.recipient.postmortumwaitlist === true) {
      this.notify.withAutodismiss('PM waitlist patients cannot have priority or extra wait time. Register as Crossover participant instead.');
      this.ref.close(false);
      return;
    }

    // Get form values
    const formValues = this.requestForm.value;

    // Convert editor content to HTML
    const editorContent = formValues.requestComment;
    const requestComment = editorContent ? toHTML(editorContent) : undefined;

    // Determine approval column based on request type
    const approvalColumn = this.requestType === 'priority'
      ? ApprovalColumn.PRIORITY
      : ApprovalColumn.EXTRAWAITTIME;

    // Create the approval request
    const approval: Approval = {
      approvalColumn: approvalColumn,
      recipient: this.recipient,
      status: ApprovalStatus.PENDING,
      requestComment: requestComment
    };

    // Add additional privileges only for priority requests
    if (this.requestType === 'priority') {
      const requestedAcceptableHla = formValues.requestedAcceptableHla?.trim() || undefined;
      const requestedAcceptableBloodTypes = this.formatAcceptableBloodTypes(formValues.requestedAcceptableBloodTypes);

      approval.requestedAcceptableHla = requestedAcceptableHla;
      approval.requestedAcceptableBloodTypes = requestedAcceptableBloodTypes;
    }

    this.participantService.createApprovalRequest(approval).subscribe({
      next: () => {
        let message: string;

        if (this.requestType === 'priority') {
          message = `Priority has been requested for recipient ${this.recipient.enisNumber}`;
          const privileges = [];
          // Acceptable HLA is now called: Delisted Unacceptables, but in the backend/database this field is still named requestedAcceptableHla
          if (approval.requestedAcceptableHla) privileges.push('Delisted Unacceptables');
          if (approval.requestedAcceptableBloodTypes) privileges.push('Acceptable Blood Types');
          if (privileges.length > 0) {
            message += ' with additional privileges: ' + privileges.join(', ');
          }
        } else {
          message = `Extra wait time has been requested for recipient ${this.recipient.enisNumber}`;
        }

        this.notify.withAutodismiss(message);
        this.ref.close(true);
      },
      error: (err) => {
        console.log(err);
        this.notify.withAutodismiss('Failed to apply request. Please try again.');
        this.ref.close(false);
      }
    });
  }
  isNumericInput(privilege: ApprovalColumn | null): boolean {
    return privilege === ApprovalColumn.MAXDONORAGE || privilege === ApprovalColumn.MAXMISMATCHES;
  }
  cancel() {
    this.ref.close(false);
  }

  /**
   * Check if the submit button should be disabled
   * Submit is disabled if:
   * - Form is invalid, OR
   * - Trying to submit priority when there's already a pending priority request, OR
   * - Trying to submit extra waittime when there's already a pending extra waittime request
   */
  isSubmitDisabled(): boolean {
    if (!this.requestForm.valid) {
      return true;
    }

    // Disable submit if trying to submit priority when there's already a pending one
    if (this.requestType === 'priority' && this.hasPendingPriorityRequest) {
      return true;
    }

    // Disable submit if trying to submit extra waittime when there's already a pending one
    if (this.requestType === 'extraWaitTime' && this.hasPendingExtraWaitTimeRequest) {
      return true;
    }

    return false;
  }

  /**
   * Get tooltip text for the submit button
   */
  getSubmitTooltip(): string {
    if (this.requestType === 'priority' && this.hasPendingPriorityRequest) {
      return 'Delete pending request first';
    }
    if (this.requestType === 'extraWaitTime' && this.hasPendingExtraWaitTimeRequest) {
      return 'Delete pending request first';
    }
    return '';
  }

  /**
   * Convert blood types array to space-separated string
   */
  private formatAcceptableBloodTypes(bloodTypes: string[]): string | undefined {
    if (!bloodTypes || bloodTypes.length === 0) return undefined;
    return bloodTypes.join(' ');
  }
}
